import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary shadow-button',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
        danger:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive shadow-button',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2 sm:h-10', // 44px minimum on mobile, 40px on desktop
        sm: 'h-9 px-3 text-xs sm:h-8', // 36px minimum on mobile, 32px on desktop
        lg: 'h-12 px-8',
        icon: 'h-11 w-11 sm:h-10 sm:w-10', // 44px minimum on mobile, 40px on desktop
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean; // Standardized prop name
  isLoading?: boolean; // Deprecated, kept for backward compatibility
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, isLoading, children, ...props }, ref) => {
    // Support both loading and isLoading for backward compatibility
    const isLoadingState = loading || isLoading;

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isLoadingState || props.disabled}
        ref={ref}
        {...props}
      >
        {isLoadingState && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
export { Button };
